import { useCallback, useEffect, useState } from 'react';
import { Icon } from './Icon';
import { Modal } from './ui';

/**
 * اختيار البطاقات المعروضة.
 *
 * الشاشة تعرف أكثر ممّا يتّسع لصدرها: للعملاء المحتملين خمسة دلاء زمنية
 * وسبع حالات، وعرضها جميعًا اثنتا عشرة بطاقة تملأ الشاشة قبل أن يظهر أول
 * صفّ. وحصرُها فيما نراه نحن مهمًّا يفرض على مديرٍ يتابع «المستبعدين»
 * ترتيبًا لا يعنيه.
 *
 * فالاختيار للمستخدم، ويُحفظ في متصفحه.
 *
 * **ولا يُحفظ في الخادم عمدًا.** تفضيلٌ بصري يخصّ الشاشة التي يجلس أمامها،
 * وحفظه في قاعدة البيانات يعني جدولًا وترحيلًا ومسارًا لتفضيل لا يُقرأ إلا
 * من متصفحه. و`localStorage` يفقده عند تغيير الجهاز — وهو ثمن مقبول لتفضيل
 * يُضبط في ثانيتين.
 */

export interface CardChoice {
  key: string;
  label: string;
  /** المجموعة في نافذة الاختيار — «حسب المرحلة» أو «حسب الحالة». */
  group: string;
}

/**
 * التفضيل المحفوظ.
 *
 * `null` تعني «لم يختر بعد» فتُعرض البطاقات الافتراضية — وهي ليست كـ«اختار
 * ألّا يعرض شيئًا»، وخلطهما يجعل من أزال كل البطاقات يراها تعود عند كل
 * تحديث.
 */
export function useCardChoice(
  storageKey: string,
  defaults: string[],
): {
  chosen: string[];
  setChosen: (keys: string[]) => void;
  reset: () => void;
} {
  const [chosen, setState] = useState<string[]>(() => read(storageKey) ?? defaults);

  /* المزامنة بين تبويبين مفتوحين على الشاشة نفسها. */
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key !== storageKey) return;
      setState(read(storageKey) ?? defaults);
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [storageKey, defaults]);

  const setChosen = useCallback(
    (keys: string[]) => {
      setState(keys);
      write(storageKey, keys);
    },
    [storageKey],
  );

  const reset = useCallback(() => {
    setState(defaults);
    write(storageKey, null);
  }, [storageKey, defaults]);

  return { chosen, setChosen, reset };
}

/*
 * القراءة والكتابة محاطتان بـtry.
 *
 * `localStorage` يرمي في التصفّح الخاص وحين يمنع المستخدم تخزين المواقع.
 * وتفضيلٌ بصري لا يستحق أن يُسقط الشاشة.
 */
function read(key: string): string[] | null {
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === 'string') : null;
  } catch {
    return null;
  }
}

function write(key: string, value: string[] | null): void {
  try {
    if (value === null) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* تجاهل: التفضيل يعمل هذه الجلسة ولا يُحفظ. */
  }
}

/**
 * يُرتّب البطاقات المتاحة بترتيب اختيار المستخدم.
 *
 * عامٌّ في نوعه لا مقصورٌ على `InsightItem`: بطاقات القوائم تحمل عددًا،
 * وبطاقات الملفّ تحمل قيمةً قد تكون اسم مسؤول أو موعدًا — والترتيب واحد.
 */
export function applyChoice<T extends { key: string }>(all: T[], chosen: string[]): T[] {
  const byKey = new Map(all.map((i) => [i.key, i]));
  return chosen.map((k) => byKey.get(k)).filter((i): i is T => !!i);
}

export function CardPickerButton({
  choices,
  chosen,
  onChange,
  onReset,
  trigger = 'bar',
  className = 'btn btn-sm btn-ghost',
}: {
  choices: CardChoice[];
  chosen: string[];
  onChange: (keys: string[]) => void;
  onReset: () => void;
  /**
   * شكل المُشغّل.
   *
   * `bar` زرٌّ في شريط الأدوات فوق قائمة، و`tile` بطاقةٌ فارغة في ذيل صفّ
   * البطاقات. والثانية لأن صفّ بطاقات الملفّ لا شريط فوقه، وزرٌّ عائم فوق
   * البطاقات يحجب أوّلها.
   */
  trigger?: 'bar' | 'tile';
  /** هيئة زرّ الشريط — رؤوس الملفات أزرارها محدودة لا شبحية فيسايرها. */
  className?: string;
}) {
  const [open, setOpen] = useState(false);

  const groups = [...new Set(choices.map((c) => c.group))];

  const toggle = (key: string) => {
    onChange(chosen.includes(key) ? chosen.filter((k) => k !== key) : [...chosen, key]);
  };

  return (
    <>
      {trigger === 'tile' ? (
        <button
          type="button"
          className="rstat-add"
          onClick={() => setOpen(true)}
          title="اختر البطاقات المعروضة"
        >
          <span className="rstat-add-icon">
            <Icon name="layers" size={16} />
          </span>
          <span className="tiny">اختر البطاقات</span>
        </button>
      ) : (
        <button
          type="button"
          className={className}
          onClick={() => setOpen(true)}
          title="اختر البطاقات المعروضة"
        >
          <Icon name="layers" size={14} />
          البطاقات
        </button>
      )}

      {open && (
        <Modal
          title="البطاقات المعروضة"
          onClose={() => setOpen(false)}
          footer={
            <>
              <button className="btn" onClick={onReset}>
                <Icon name="refresh" size={14} />
                الافتراضي
              </button>
              <button className="btn btn-primary" onClick={() => setOpen(false)}>
                تمّ
              </button>
            </>
          }
        >
          <p className="tiny mute mb-4">
            اختر ما تريد رؤيته في صدر الشاشة. الترتيب يتبع ترتيب اختيارك، والتفضيل
            يُحفظ في هذا المتصفح.
          </p>

          {groups.map((group) => (
            <div key={group} className="mb-4">
              <h3 className="card-title mb-2">{group}</h3>
              <div className="pick-list">
                {choices
                  .filter((c) => c.group === group)
                  .map((c) => {
                    const on = chosen.includes(c.key);
                    return (
                      <button
                        key={c.key}
                        type="button"
                        className={`pick-row${on ? ' pick-row-current' : ''}`}
                        onClick={() => toggle(c.key)}
                        aria-pressed={on}
                      >
                        <span className="pick-row-text">
                          <strong>{c.label}</strong>
                        </span>
                        {on ? (
                          <Icon name="check" size={16} />
                        ) : (
                          <span className="tiny mute">مخفية</span>
                        )}
                      </button>
                    );
                  })}
              </div>
            </div>
          ))}

          {chosen.length === 0 && (
            <div className="alert alert-info">
              لا بطاقات مختارة — سيبدأ صدر الشاشة بالقائمة مباشرةً.
            </div>
          )}
        </Modal>
      )}
    </>
  );
}
